<template>
  <PageShell :background="pageBackground" wide>
    <PageHeader size="page" align="start" :title="t('whatIsIt.title')" :lead="t('whatIsIt.lead')">
      <template #actions>
        <LandingButton to="/register" variant="accent">{{ t('whatIsIt.cta.register') }}</LandingButton>
        <LandingButton to="/faq/player-cheatsheet" variant="ghost">
          {{ t('whatIsIt.cta.cheatSheet') }}
        </LandingButton>
      </template>
    </PageHeader>

    <!-- 1. What Dimension-X is -->
    <PageSection :title="t('whatIsIt.what.title')">
      <div class="split">
        <Prose>
          <p>{{ t('whatIsIt.what.p1') }}</p>
          <p>{{ t('whatIsIt.what.p2') }}</p>
        </Prose>
        <Figure
          eager
          zoomable
          :src="sessionFigure.src"
          :alt="sessionFigure.alt"
          :caption="sessionFigure.caption"
          :zoom-label="t('whatIsIt.zoom.trigger')"
          @zoom="openZoom(sessionFigure)"
        />
      </div>

      <Accordion :items="newcomerAside">
        <template #answer>
          <Prose>
            <p>{{ t('whatIsIt.newcomer.p1') }}</p>
            <p>{{ t('whatIsIt.newcomer.p2') }}</p>
          </Prose>
        </template>
      </Accordion>
    </PageSection>

    <!-- 2. Why it is interesting -->
    <PageSection :title="t('whatIsIt.why.title')" :lead="t('whatIsIt.why.lead')">
      <MediaGrid min="18rem">
        <Card v-for="card in whyCards" :key="card.title" as="li" dense>
          <h3 class="card-title">{{ card.title }}</h3>
          <Prose>
            <p>{{ card.text }}</p>
          </Prose>
        </Card>
      </MediaGrid>

      <div class="split split--reverse">
        <Prose>
          <p>{{ t('whatIsIt.why.p1') }}</p>
          <p><strong>{{ t('whatIsIt.why.p2') }}</strong></p>
        </Prose>
        <Figure
          zoomable
          :src="compendiumFigure.src"
          :alt="compendiumFigure.alt"
          :caption="compendiumFigure.caption"
          :zoom-label="t('whatIsIt.zoom.trigger')"
          @zoom="openZoom(compendiumFigure)"
        />
      </div>
    </PageSection>

    <!-- 3. What playing looks like -->
    <PageSection :title="t('whatIsIt.turn.title')" :lead="t('whatIsIt.turn.lead')">
      <div class="split">
        <div class="stack">
          <StepList :steps="turnSteps" />
          <Prose>
            <p>
              <i18n-t keypath="whatIsIt.turn.cheatSheet.text" tag="span">
                <template #link>
                  <router-link to="/faq/player-cheatsheet">
                    {{ t('whatIsIt.turn.cheatSheet.linkText') }}
                  </router-link>
                </template>
              </i18n-t>
            </p>
          </Prose>
        </div>
        <Figure
          zoomable
          ratio="2 / 1"
          :src="diceFigure.src"
          :alt="diceFigure.alt"
          :caption="diceFigure.caption"
          :zoom-label="t('whatIsIt.zoom.trigger')"
          @zoom="openZoom(diceFigure)"
        />
      </div>

      <Figure
        zoomable
        ratio="16 / 9"
        :src="tacticalFigure.src"
        :alt="tacticalFigure.alt"
        :caption="tacticalFigure.caption"
        :zoom-label="t('whatIsIt.zoom.trigger')"
        @zoom="openZoom(tacticalFigure)"
      />
    </PageSection>

    <!-- 4. The world -->
    <PageSection :title="t('whatIsIt.world.title')">
      <div class="split split--reverse">
        <div class="stack">
          <Prose>
            <p>{{ t('whatIsIt.world.p1') }}</p>
            <p>{{ t('whatIsIt.world.p2') }}</p>
          </Prose>
          <div class="actions">
            <LandingButton to="/art-gallery" variant="ghost">{{ t('whatIsIt.cta.gallery') }}</LandingButton>
            <LandingButton to="/books/preview" variant="ghost">{{ t('whatIsIt.cta.books') }}</LandingButton>
            <LandingButton to="/community" variant="ghost">{{ t('whatIsIt.cta.community') }}</LandingButton>
          </div>
        </div>

        <Figure
          zoomable
          :src="worldFigure.src"
          :alt="worldFigure.alt"
          :caption="worldFigure.caption"
          :zoom-label="t('whatIsIt.zoom.trigger')"
          @zoom="openZoom(worldFigure)"
        />
      </div>
    </PageSection>

    <!-- 5. Player or Game Master -->
    <PageSection :title="t('whatIsIt.roles.title')" :lead="t('whatIsIt.roles.lead')">
      <div class="split split--stretch">
        <Card v-for="role in roles" :key="role.title" variant="accent">
          <h3 class="card-title">{{ role.title }}</h3>
          <Prose>
            <p>{{ role.text }}</p>
          </Prose>
          <Figure
            zoomable
            class="card-figure"
            :src="role.src"
            :alt="role.alt"
            :caption="role.caption"
            :zoom-label="t('whatIsIt.zoom.trigger')"
            @zoom="openZoom(role)"
          />
        </Card>
      </div>
    </PageSection>

    <!-- 6. How to start -->
    <PageSection :title="t('whatIsIt.start.title')" :lead="t('whatIsIt.start.lead')">
      <div class="split">
        <div class="stack">
          <StepList :steps="startSteps">
            <template #step="{ step }">
              <i18n-t v-if="step.link" :keypath="step.keypath" tag="span">
                <template #link>
                  <router-link :to="step.link">{{ step.linkText }}</router-link>
                </template>
              </i18n-t>
              <template v-else>{{ step.text }}</template>
            </template>
          </StepList>

          <Prose>
            <p>
              <i18n-t keypath="whatIsIt.start.discordLine.text" tag="span">
                <template #link>
                  <a :href="discordUrl" target="_blank" rel="noopener noreferrer">
                    {{ t('whatIsIt.start.discordLine.linkText') }}
                  </a>
                </template>
              </i18n-t>
            </p>
          </Prose>

          <div class="actions">
            <LandingButton to="/register" variant="accent">{{ t('whatIsIt.cta.register') }}</LandingButton>
            <LandingButton to="/faq/player-cheatsheet" variant="ghost">
              {{ t('whatIsIt.cta.cheatSheet') }}
            </LandingButton>
          </div>
        </div>

        <Figure
          zoomable
          ratio="2 / 1"
          :src="startFigure.src"
          :alt="startFigure.alt"
          :caption="startFigure.caption"
          :zoom-label="t('whatIsIt.zoom.trigger')"
          @zoom="openZoom(startFigure)"
        />
      </div>
    </PageSection>

    <Lightbox
      :open="Boolean(zoomed)"
      :src="zoomed?.src || ''"
      :alt="zoomed?.alt || ''"
      :caption="zoomed?.caption || ''"
      :label="t('whatIsIt.zoom.label')"
      :close-label="t('whatIsIt.zoom.close')"
      @close="zoomed = null"
    />
  </PageShell>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import PageShell from '@/components/public/PageShell.vue';
import PageHeader from '@/components/public/PageHeader.vue';
import PageSection from '@/components/public/PageSection.vue';
import Prose from '@/components/public/Prose.vue';
import Card from '@/components/public/Card.vue';
import Accordion from '@/components/public/Accordion.vue';
import StepList from '@/components/public/StepList.vue';
import Figure from '@/components/public/Figure.vue';
import Lightbox from '@/components/public/Lightbox.vue';
import MediaGrid from '@/components/public/MediaGrid.vue';
import LandingButton from '@/components/btn/LandingButton.vue';

import pageBackground from '@/assets/images/backgrounds/common/wasteland-b.webp';
import GameplayExampleImg from '@/assets/images/faq/faq-gameplay-example.webp';
import GameMasterCompendiumImg from '@/assets/what-is-it/GameMasterCompendium.webp';
import DiceLabImg from '@/assets/images/faq/faq-dice-lab.webp';
import TacticalAimImg from '@/assets/what-is-it/TacticalAim.webp';
import PlayerCharacterCardImg from '@/assets/what-is-it/PlayerCharacterCard.webp';
import GameMasterAreaImg from '@/assets/what-is-it/GameMasterArea.webp';
import DimensionThreeImg from '@/assets/images/dx-3.webp';
import CreationPathImg from '@/assets/images/faq/faq-creation-path.webp';

const { t } = useI18n();

const discordUrl = 'https://discord.gg/vC3TvVzK';

const zoomed = ref(null);
const openZoom = (figure) => {
  zoomed.value = figure;
};

const figure = (src, base) =>
  computed(() => ({
    src,
    alt: t(`${base}.imageAlt`),
    caption: t(`${base}.imageCaption`),
  }));

const sessionFigure = figure(GameplayExampleImg, 'whatIsIt.what');
const compendiumFigure = figure(GameMasterCompendiumImg, 'whatIsIt.why');
const diceFigure = figure(DiceLabImg, 'whatIsIt.turn');
const tacticalFigure = figure(TacticalAimImg, 'whatIsIt.turn.tactical');
const worldFigure = figure(DimensionThreeImg, 'whatIsIt.world');
const startFigure = figure(CreationPathImg, 'whatIsIt.start');

const newcomerAside = computed(() => [{ id: 'newcomer', question: t('whatIsIt.newcomer.question') }]);

const whyCards = computed(() =>
  ['gm', 'skills', 'dimensions', 'automated'].map((key) => ({
    title: t(`whatIsIt.why.cards.${key}.title`),
    text: t(`whatIsIt.why.cards.${key}.text`),
  }))
);

const turnSteps = computed(() =>
  ['scene', 'declare', 'dice', 'react'].map((key) => ({
    title: t(`whatIsIt.turn.steps.${key}.title`),
    text: t(`whatIsIt.turn.steps.${key}.text`),
  }))
);

const roles = computed(() =>
  [
    ['player', PlayerCharacterCardImg],
    ['gameMaster', GameMasterAreaImg],
  ].map(([key, src]) => ({
    src,
    title: t(`whatIsIt.roles.${key}.title`),
    text: t(`whatIsIt.roles.${key}.text`),
    alt: t(`whatIsIt.roles.${key}.imageAlt`),
    caption: t(`whatIsIt.roles.${key}.imageCaption`),
  }))
);

const startSteps = computed(() => [
  {
    title: t('whatIsIt.start.steps.register.title'),
    keypath: 'whatIsIt.start.steps.register.text',
    link: '/register',
    linkText: t('whatIsIt.start.steps.register.linkText'),
  },
  ...['discord', 'character', 'play'].map((key) => ({
    title: t(`whatIsIt.start.steps.${key}.title`),
    text: t(`whatIsIt.start.steps.${key}.text`),
  })),
]);
</script>

<style scoped>
.split {
  display: grid;
  gap: var(--space-6);
  align-items: start;
}

.split--stretch {
  align-items: stretch;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.card-title {
  font-size: var(--text-lg);
  line-height: var(--leading-snug);
  margin: 0 0 var(--space-3);
}

.card-figure {
  margin-top: var(--space-4);
}

@media (min-width: 960px) {
  .split {
    grid-template-columns: 1fr 1fr;
    gap: var(--space-7);
  }

  .split--reverse > :last-child {
    order: -1;
  }
}
</style>
